import { type BoardView, type TaskView } from '@apm/contracts';
import { isOverdue } from '../../lib/overdue';

/**
 * Lọc/làm nổi thẻ trên board — APM-BOARD-f.
 *
 * ## Hai chế độ, và vì sao không gộp làm một
 *
 * - **Làm nổi**: thẻ khớp nổi lên, thẻ không khớp **vẫn còn đó** nhưng chìm
 *   xuống. Dùng khi câu hỏi là "việc của Bảo nằm rải đâu trên board" — bố cục
 *   board không đổi, nên vẫn thấy chúng đứng cạnh việc của ai.
 * - **Lọc**: chỉ còn thẻ khớp. Dùng khi câu hỏi là "chỉ đọc việc backend thôi".
 *
 * Gộp thành một chế độ thì mất một trong hai câu hỏi đó: lọc luôn thì không còn
 * ngữ cảnh xung quanh, mà chỉ làm nổi thì một board bốn mươi thẻ vẫn là bốn
 * mươi thẻ phải đọc.
 *
 * ## ⚠️ Lọc là chuyện của **lớp vẽ**, không phải của dnd
 *
 * Chỉ số thả (`dropIndex`) phải tính trên danh sách **đầy đủ**: người dùng thấy
 * ba thẻ nhưng cột có mười, và gửi chỉ số của danh sách đã lọc lên server là
 * xếp thẻ vào sai chỗ trong chín thẻ còn lại — sai một cách im lặng, vì trên
 * màn hình đã lọc thì trông vẫn đúng.
 */
export interface BoardFilter {
  readonly mode: 'highlight' | 'filter';
  /** `null` = mọi người. Ghép theo `assigneeId`, không theo tên. */
  readonly assigneeId: string | null;
  /** `null` = mọi loại việc. Ghép theo id, không theo tên. */
  readonly taskTypeId: string | null;
  /** `null` = mọi thời hạn. Xem `DueBucket`. */
  readonly due: DueBucket | null;
}

/**
 * Ba ngăn thời hạn — một **phân hoạch** của board, không phải ba bộ lọc rời.
 *
 * Mỗi thẻ rơi vào đúng một ngăn, nên chọn lần lượt ba ngăn là thấy hết board,
 * không thẻ nào lọt và không thẻ nào đếm hai lần. Việc **chưa đặt hạn** nằm ở
 * `upcoming`: câu hỏi của ngăn đó là "cái gì chưa gấp", và một việc không có
 * hạn thì đúng là chưa gấp — xếp nó ra ngoài cả ba ngăn sẽ khiến nó biến mất
 * khỏi mọi lựa chọn, tức lọc gì cũng không thấy nó đâu.
 */
export type DueBucket = 'upcoming' | 'today' | 'overdue';

export const NO_BOARD_FILTER: BoardFilter = {
  mode: 'highlight',
  assigneeId: null,
  taskTypeId: null,
  due: null,
};

/** Có tiêu chí nào đang chọn không. Không có thì đừng làm mờ hay giấu gì cả. */
export function isFilterActive(filter: BoardFilter): boolean {
  return filter.assigneeId !== null || filter.taskTypeId !== null || filter.due !== null;
}

/**
 * Thẻ này thuộc ngăn thời hạn nào, tính tại ngày `today`.
 *
 * Dùng chung định nghĩa "quá hạn" với `lib/overdue.ts` — cùng luật mà backend
 * dùng cho bộ lọc `overdue`. Việc **đã xong** không bao giờ là quá hạn (kể cả
 * xong muộn): nó thuộc báo cáo hồi tố, không thuộc danh sách phải xử lý hôm nay.
 */
export function dueBucketOf(task: TaskView, today: string): DueBucket {
  if (isOverdue(task.dueDate, task.statusCategory, today)) return 'overdue';
  if (task.dueDate === today && task.statusCategory !== 'done') return 'today';
  return 'upcoming';
}

/**
 * Thẻ này có khớp tiêu chí đang chọn không.
 *
 * Hai tiêu chí ghép bằng phép **và**: chọn cả người lẫn loại thì phải khớp cả
 * hai. Mỗi trục chọn một giá trị (cùng hình dạng với ô chọn người), nên không
 * có phép hoặc nào trong này.
 */
export function matchesFilter(task: TaskView, filter: BoardFilter, today: string): boolean {
  if (filter.assigneeId !== null && task.assigneeId !== filter.assigneeId) return false;
  if (filter.taskTypeId !== null && task.taskTypeId !== filter.taskTypeId) return false;
  if (filter.due !== null && dueBucketOf(task, today) !== filter.due) return false;
  return true;
}

/** Người phụ trách có mặt trên board, đã lọc trùng và sắp theo tên. */
export function assigneesOf(board: BoardView): { id: string; name: string }[] {
  if (board.filterOptions) return board.filterOptions.assignees;
  const byId = new Map<string, string>();
  for (const column of board.columns) {
    for (const task of column.tasks) {
      if (task.assigneeId && task.assigneeName) byId.set(task.assigneeId, task.assigneeName);
    }
  }
  return [...byId.entries()]
    .map(([id, name]) => ({ id, name }))
    .sort((a, b) => a.name.localeCompare(b.name, 'vi'));
}

/**
 * Loại việc có mặt trên board — đúng cái nhãn màu đang hiện trên từng thẻ.
 *
 * Lấy từ thẻ chứ không từ danh mục của dự án: một dự án khai mười loại nhưng
 * board sprint này chỉ dùng ba, và bảy nút không bao giờ khớp gì chỉ làm hàng
 * nút dài ra.
 */
export function taskTypesOf(
  board: BoardView,
): { id: string; name: string; color: string | null }[] {
  if (board.filterOptions) return board.filterOptions.taskTypes;
  const byId = new Map<string, { id: string; name: string; color: string | null }>();
  for (const column of board.columns) {
    for (const task of column.tasks) {
      // Mốc không có loại việc (APM-TASK-ms2a) — không thành một mục lọc.
      if (task.taskTypeId === null || task.taskTypeName === null) continue;
      byId.set(task.taskTypeId, {
        id: task.taskTypeId,
        name: task.taskTypeName,
        color: task.taskTypeColor,
      });
    }
  }
  return [...byId.values()].sort((a, b) => a.name.localeCompare(b.name, 'vi'));
}

/** Id những thẻ **không** khớp — dùng để làm chìm ở chế độ "làm nổi". */
export function dimmedIdsOf(
  board: BoardView,
  filter: BoardFilter,
  today: string,
): ReadonlySet<string> {
  if (filter.mode !== 'highlight' || !isFilterActive(filter)) return new Set();

  const dimmed = new Set<string>();
  for (const column of board.columns) {
    for (const task of column.tasks) {
      if (!matchesFilter(task, filter, today)) dimmed.add(task.id);
    }
  }
  return dimmed;
}

/**
 * Việc này có **chặn** không cho bắt đầu không — APM-BOARD-g.
 *
 * ⚠️ **Không thay cửa ở server**, chỉ trả lời sớm hơn. Server mới là chỗ chặn
 * thật (`checkCanStart`), vì kéo thả không phải đường duy nhất tới đổi trạng
 * thái. Ở đây chỉ kiểm điều kiện mà board **nhìn thấy được**; điều kiện còn lại
 * (việc phụ thuộc chưa xong) cần đồ thị phụ thuộc mà `BoardView` không mang —
 * thẻ vẫn kéo được và server trả lời, đúng một vòng mạng cho một trường hợp
 * hiếm hơn.
 *
 * Thiếu **ước lượng** không nằm ở đây: nó là cảnh báo, không phải cửa chặn —
 * xem `checkCanStart`. Board vẫn nhắc bằng `warnsBeforeStart` bên dưới.
 */
export function blocksStart(task: TaskView): 'no_assignee' | null {
  return task.assigneeId === null ? 'no_assignee' : null;
}

/**
 * Việc này bắt đầu được, nhưng có gì đáng nhắc không.
 *
 * Tách khỏi `blocksStart` vì hai kết cục khác hẳn nhau: một cái **huỷ** cú kéo,
 * một cái để cú kéo đi tiếp rồi mới nói. Gộp chung thì chỗ gọi phải tự nhớ mã
 * nào thuộc loại nào, và quên một lần là chặn nhầm.
 */
export function warnsBeforeStart(task: TaskView): 'no_estimate' | null {
  return task.estimateHours === null ? 'no_estimate' : null;
}
